import api from '@api/api';
import dayjs from 'dayjs';

const dashboardRules: { [key: string]: (...args: any[]) => boolean } = {
  // 分子不可大於分母
  'numerator-cannot-be-greater-than-denominator': (
    validateParameters: number[],
  ) => {
    const [numerator, denominator] = validateParameters;
    return numerator <= denominator;
  },
  // 分子、分母不可為負數
  'numerator-and-denominator-cannot-be-negative': (
    validateParameters: number[],
  ) => {
    const [numerator, denominator] = validateParameters;
    return numerator >= 0 && denominator >= 0;
  },
  // 稼動率總時間不可大於現在時間
  'maximumMinutesMap-cannot-be-greater-than-current-time': (
    validateParameters: number[],
  ) => {
    const [maximumMinutesMap, currentTime] = validateParameters;
    return maximumMinutesMap <= currentTime;
  },
  'totalChargingGunCount-cannot-be-greater-than-memberCount': (
    validateParameters: number[],
  ) => {
    const [totalChargingGunCount, memberCount] = validateParameters;
    return totalChargingGunCount <= memberCount;
  },
  'parkingSpaceCount-cannot-be-smaller-than-basicInfoPileCount': (
    validateParameters: number[],
  ) => {
    const [parkingSpaceCount, basicInfoPileCount] = validateParameters;
    // 使用更明確的比較邏輯
    const gunCount = Number(parkingSpaceCount);
    const pileCount = Number(basicInfoPileCount);

    const difference = gunCount - pileCount;
    return difference >= 0;
  },
  'totalPileCount-cannot-be-greater-than-basicInfoPileCount': (
    validateParameters: number[],
  ) => {
    const [totalPileCount, basicInfoPileCount] = validateParameters;
    return totalPileCount <= basicInfoPileCount;
  },
  'duration-cannot-be-greater-than-maxDuration_multiplied_by_totalBillCount': (
    validateParameters: number[],
  ) => {
    const [duration, maxDuration] = validateParameters;
    return duration <= maxDuration;
  },
  'pileMaxmunPower-cannot-be-greater-than-commonGunPower-multiplied-by-chargingPerformanceDuration':
    (validateParameters: number[]) => {
      const [pileMaxmunPower, commonGunPower] = validateParameters;
      return pileMaxmunPower <= commonGunPower;
    },
  'incomeStatisticsMoney-cannot-be-greater-than-mostExpensiveElectricityFee-multiplied-by-pileOutputPower':
    (validateParameters: number[]) => {
      const [incomeStatisticsMoney, maximumMoney] = validateParameters;
      return incomeStatisticsMoney <= maximumMoney;
    },
};

type TDashboardRuleKey = keyof typeof dashboardRules;
type TDashboardRules = TDashboardRuleKey[] | TDashboardRuleKey;

interface IFailedDetail {
  rule: TDashboardRuleKey;
  validateParameters: number[] | number[][];
  endPoints: string[][] | string[] | string;
  validateKeys: string[] | string[][];
  errorValue: { [key: string]: number };
  occurrentTime: string;
  hostname: string;
}

function errorHandler(errorMsg: string) {
  console.error(errorMsg);
}

function createVerifier() {
  let hasSentFailedDetails = false;

  async function sendFailedDetails(failedDetails: IFailedDetail[]) {
    console.log(failedDetails);
    const { endPoints, errorValue, occurrentTime, rule, hostname } =
      failedDetails[0];
    const requestData = {
      Rule: rule,
      API: Array.isArray(endPoints) ? endPoints.flat().join(', ') : endPoints, // Updated this line
      'Error value': errorValue,
      'Occurrence time': occurrentTime,
      hostname,
    };
    try {
      await api.post(
        'api/statistics-service/notification/sendDashboardErrorEmail',
        requestData,
      );
    } catch (error) {
      console.error('Failed to send failed details:', error);
      hasSentFailedDetails = false;
    }

    hasSentFailedDetails = true;
  }

  return function verifyDashboardRules(
    rules: TDashboardRules,
    endPoints: string[][] | string[], // Updated this line
    validateParameters: number[][] | number[],
    validateKeys: string[][] | string[],
  ) {
    const currentUrlHostname = window.location.hostname;
    if (
      currentUrlHostname === 'yds-dev.beseye.com' ||
      process.env.NODE_ENV === 'development'
    ) {
      console.log('驗證規則已在 dev 環境跳過');
      return;
    }

    if (hasSentFailedDetails) {
      console.log('已經發送過失敗詳情，不再進行驗證');
      return;
    }

    if (!rules || (typeof rules !== 'string' && !Array.isArray(rules))) {
      errorHandler('Invalid Rules');
      return;
    }

    const iteratedRules = Array.isArray(rules) ? rules : [rules];
    const iteratedEndPoints = Array.isArray(endPoints[0][0])
      ? (endPoints as string[][])
      : (endPoints as string[]);
    const iteratedValidateParameters = Array.isArray(validateParameters[0])
      ? (validateParameters as number[][])
      : [validateParameters as number[]];
    const iteratedValidateKeys = Array.isArray(validateKeys[0])
      ? (validateKeys as string[][])
      : [validateKeys as string[]];

    if (
      (Array.isArray(iteratedEndPoints[0][0])
        ? iteratedEndPoints[0][0].length !== iteratedValidateParameters.length
        : iteratedEndPoints.length !== iteratedValidateParameters.length) ||
      iteratedValidateParameters.length !== iteratedValidateKeys.length
    ) {
      errorHandler(
        'The number of endPoints, validateParameters, and validateKeys must match.',
      );
      return;
    }

    const failedDetails: IFailedDetail[] = [];

    iteratedValidateParameters.forEach((params, idx) => {
      iteratedRules.forEach((rule) => {
        if (!dashboardRules[rule]) {
          errorHandler(`Invalid Rule: ${rule}`);
          return;
        }

        if (params.length !== 2 || iteratedValidateKeys[idx].length !== 2) {
          errorHandler('Invalid validateParameters or validateKeys structure.');
          return;
        }

        const result = dashboardRules[rule](params);
        if (!result) {
          const errorValueOfRule: { [key: string]: number } = {};
          iteratedValidateKeys[idx].forEach((key, index) => {
            errorValueOfRule[key] = params[index];
          });

          failedDetails.push({
            rule,
            validateParameters: params,
            endPoints: Array.isArray(iteratedEndPoints[0][0])
              ? iteratedEndPoints[0][idx]
              : iteratedEndPoints[idx],
            validateKeys: iteratedValidateKeys[idx],
            errorValue: errorValueOfRule,
            occurrentTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            hostname: currentUrlHostname,
          });
        }
      });
    });

    if (!failedDetails.length) {
      console.log('No failed details');
    }
    if (failedDetails.length === 1) {
      sendFailedDetails(failedDetails);
    }
  };
}

export default createVerifier;
